import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  useOrganizationQuery,
  useOutgoingMailboxQuery,
  useUpdateAutoEmailSendingModeMutation,
  useUserInfoQuery,
  useUpdateUsernameMutation,
  useDeleteUserDataMutation,
  type DeleteUserDataScope,
} from "../api/queries";
import {
  useDefaultLanguageQuery,
  useUpdateAllCustomersLanguageMutation,
  useUpdateOrgMfaMutation,
  useUpdateOrgMcpMutation,
  useUpdateCollectionEmailMutation,
  useUpdateDsoSettingsMutation,
} from "../api/queries/organizationQueries";
import { useMailboxSyncNow } from "../hooks/useMailboxSyncNow";
import apiClient from "../api/apiClient";
import { Tabs } from "@mantine/core";
import type { AutoEmailSendingMode } from "../utils/types";
import { toast } from "sonner";
import { EmailSignatureSettings } from "./EmailSignatureSettings";
import {
  IconMail,
  IconShieldLock,
  IconMailbox,
  IconSignature,
  IconTrash,
  IconUser,
  IconUsers,
  IconChartBar,
  IconBan,
  IconEscalatorUp,
} from "@tabler/icons-react";
import { AccountProfileSubTab } from "./AccountProfileSubTab";
import { EmailGeneralSettingsSubTab } from "./EmailGeneralSettingsSubTab";
import { EmailModeSettingsSubTab } from "./EmailModeSettingsSubTab";
import { EmailSyncSubTab } from "./EmailSyncSubTab";
import { DsoSettingsSubTab } from "./DsoSettingsSubTab";
import { useAuth } from "../hooks/useAuth";
import { AdminScope } from "./auth/AdminScope";
import { ManagerScope } from "./auth/ManagerScope";
import { OrgUsersPage } from "./orgUsers/OrgUsersPage";
import { BlockedCustomersSection } from "./BlockedCustomersSection";
import { EscalationSettingsTab } from "./EscalationSettingsTab";

function SecurityMfaSection({
  mfaEnabled,
  isUpdating,
  onToggle,
}: {
  mfaEnabled: boolean;
  isUpdating: boolean;
  onToggle: (enabled: boolean) => void;
}) {
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [confirmEnable, setConfirmEnable] = useState(false);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-800 mb-1">Security</h2>
        <p className="text-sm text-gray-500">
          Manage multi-factor authentication for your organization
        </p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-base font-semibold text-gray-800">
              Require MFA for all users
            </h3>
            <p className="mt-1 text-sm text-gray-500 leading-relaxed">
              When enabled, every user in your organization must set up and
              verify a TOTP authenticator app (e.g. Google Authenticator) before
              they can access the application. Users who haven't enrolled will
              be guided through setup on their next login.
            </p>
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={mfaEnabled}
            disabled={isUpdating}
            onClick={() => {
              if (mfaEnabled) {
                setConfirmDisable(true);
              } else {
                setConfirmEnable(true);
              }
            }}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:opacity-50 ${
              mfaEnabled ? "bg-emerald-600" : "bg-gray-200"
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                mfaEnabled ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
        </div>

        {mfaEnabled && (
          <div className="mt-4 flex items-center gap-2 rounded-lg bg-emerald-50 px-4 py-2.5 text-sm text-emerald-700">
            <IconShieldLock size={16} stroke={2} />
            MFA is currently enforced for all users in this organization.
          </div>
        )}
      </div>

      {confirmDisable && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-5">
          <p className="text-sm font-medium text-red-800">
            Are you sure you want to disable MFA?
          </p>
          <p className="mt-1 text-sm text-red-600">
            Users will no longer be required to verify with an authenticator
            app. Existing enrollments are preserved and will be enforced again
            if MFA is re-enabled.
          </p>
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              disabled={isUpdating}
              onClick={() => {
                onToggle(false);
                setConfirmDisable(false);
              }}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {isUpdating ? "Disabling..." : "Yes, disable MFA"}
            </button>
            <button
              type="button"
              onClick={() => setConfirmDisable(false)}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {confirmEnable && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-lg rounded-xl border border-amber-200 bg-white p-6 shadow-2xl">
            <h4 className="text-base font-semibold text-gray-900">
              Enable MFA for all users?
            </h4>
            <p className="mt-2 text-sm text-gray-600 leading-relaxed">
              Once enabled, every user in your organization will be required to
              enroll and verify a TOTP authenticator app before accessing the
              application.
            </p>
            <p className="mt-2 text-sm text-gray-600 leading-relaxed">
              Users who are not enrolled will be prompted to complete setup on
              their next login.
            </p>
            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmEnable(false)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isUpdating}
                onClick={() => {
                  onToggle(true);
                  setConfirmEnable(false);
                }}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
              >
                {isUpdating ? "Enabling..." : "Yes, enable MFA"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SecurityMcpSection({
  mcpEnabled,
  hasPriorityUrl,
  isUpdating,
  canMutate,
  onToggle,
}: {
  mcpEnabled: boolean;
  hasPriorityUrl: boolean;
  isUpdating: boolean;
  canMutate: boolean;
  onToggle: (enabled: boolean) => void;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h3 className="text-base font-semibold text-gray-800">
            Enable MCP for this organization
          </h3>
          <p className="mt-1 text-sm text-gray-500 leading-relaxed">
            When enabled, authorized MCP clients can call this organization&apos;s
            tools. Priority tools query the connected Priority company over OData
            (only forms enabled under Limited Access / API Forms).
          </p>
          {!hasPriorityUrl && (
            <p className="mt-2 text-sm text-amber-700">
              Priority is not connected, so Priority MCP tools will return an
              error until credentials are configured.
            </p>
          )}
        </div>

        <button
          type="button"
          role="switch"
          aria-checked={mcpEnabled}
          disabled={!canMutate || isUpdating}
          onClick={() => {
            if (!canMutate) return;
            onToggle(!mcpEnabled);
          }}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
            canMutate ? "cursor-pointer" : "cursor-not-allowed"
          } ${mcpEnabled ? "bg-emerald-600" : "bg-gray-200"}`}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              mcpEnabled ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
      </div>
    </div>
  );
}

export default function SettingsCenter() {
  const [searchParams] = useSearchParams();
  const { roles, logout, canMutateOrg } = useAuth();
  const { data: organization } = useOrganizationQuery();
  const { data: outgoingMailbox } = useOutgoingMailboxQuery();
  const { mutate: updateAutoEmailMode, isPending: isUpdatingAutoEmailMode } =
    useUpdateAutoEmailSendingModeMutation();
  const defaultLanguageQuery = useDefaultLanguageQuery();
  const updateAllCustomersLanguageMutation =
    useUpdateAllCustomersLanguageMutation();
  const { data: userInfo } = useUserInfoQuery();
  const { mutate: updateUsername, isPending: isUpdatingUsername } =
    useUpdateUsernameMutation();
  const deleteUserDataMutation = useDeleteUserDataMutation();
  const {
    syncStatus,
    isProcessingEmails,
    handleSyncNow: handleTriggerMailboxSync,
  } = useMailboxSyncNow();
  const { mutate: updateOrgMfa, isPending: isUpdatingMfa } =
    useUpdateOrgMfaMutation();
  const { mutate: updateOrgMcp, isPending: isUpdatingMcp } =
    useUpdateOrgMcpMutation();
  const { mutate: updateCollectionEmail, isPending: isSavingCollectionEmail } =
    useUpdateCollectionEmailMutation();
  const { mutate: updateDsoSettings, isPending: isUpdatingDsoSettings } =
    useUpdateDsoSettingsMutation();
  const [autoSendMode, setAutoSendMode] = useState<AutoEmailSendingMode | null>(
    organization?.auto_email_sending_mode ?? null,
  );
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(
    defaultLanguageQuery.data?.language ?? null,
  );
  const [deleteScope, setDeleteScope] = useState<DeleteUserDataScope | null>(
    null,
  );
  const [deletePhrase, setDeletePhrase] = useState("");
  const [deleteAcknowledge, setDeleteAcknowledge] = useState(false);
  const [useSharedMailboxDraft, setUseSharedMailboxDraft] = useState(false);
  const [officialEmailDraft, setOfficialEmailDraft] = useState("");

  useEffect(() => {
    setAutoSendMode(organization?.auto_email_sending_mode ?? null);
  }, [organization?.auto_email_sending_mode]);

  useEffect(() => {
    if (defaultLanguageQuery.data?.language) {
      setSelectedLanguage(defaultLanguageQuery.data.language);
    }
  }, [defaultLanguageQuery.data?.language]);

  useEffect(() => {
    if (!outgoingMailbox) {
      return;
    }
    setUseSharedMailboxDraft(Boolean(outgoingMailbox.uses_official_email));
    setOfficialEmailDraft(outgoingMailbox.official_outgoing_email ?? "");
  }, [outgoingMailbox]);

  const handleSaveAutoSendMode = () => {
    if (!organization || !autoSendMode) {
      return;
    }

    updateAutoEmailMode(
      {
        auto_email_sending_mode: autoSendMode,
      },
      {
        onSuccess: () =>
          toast.success("Auto-send preference updated successfully"),
      },
    );
  };

  const handleSaveDsoSettings = (daysSales: number, period: number) => {
    updateDsoSettings(
      { days_sales: daysSales, period },
      {
        onSuccess: () => toast.success("DSO settings updated successfully"),
        onError: () => toast.error("Failed to update DSO settings"),
      },
    );
  };

  const handleUpdateAllCustomersLanguage = () => {
    if (!selectedLanguage) {
      return;
    }
    updateAllCustomersLanguageMutation.mutate(selectedLanguage);
  };

  const handleSaveUsername = (username: string) => {
    updateUsername(username, {
      onSuccess: () => toast.success("Display name updated"),
      onError: (error: any) => {
        const detail = error?.response?.data?.detail;
        const description =
          typeof detail === "string"
            ? detail
            : Array.isArray(detail) && detail[0]?.msg
              ? detail[0].msg
              : "Failed to update display name.";
        toast.error("Error", { description });
      },
    });
  };

  const handleSaveEmailMode = () => {
    updateCollectionEmail(
      {
        use_official_outgoing_email: useSharedMailboxDraft,
        official_outgoing_email: useSharedMailboxDraft
          ? officialEmailDraft.trim() || null
          : officialEmailDraft.trim() || null,
      },
      {
        onSuccess: (data) => {
          if (data.issue_message) {
            toast.warning("Email mode saved", {
              description: data.issue_message,
            });
          } else {
            toast.success("Email mode updated");
          }
        },
        onError: (error: any) => {
          const detail = error?.response?.data?.detail;
          const description =
            typeof detail === "string"
              ? detail
              : typeof detail === "object" && detail?.message
                ? detail.message
                : "Failed to update email mode.";
          toast.error("Error", { description });
        },
      },
    );
  };

  const handleReAuthenticateEmail = async () => {
    if (!userInfo?.oauth_provider) {
      toast.error("No email provider configured");
      return;
    }

    try {
      const provider =
        userInfo.oauth_provider === "google" ? "google" : "microsoft";
      const response = await apiClient.get(`/user/${provider}_oauth_url`);
      const { authorization_url } = response.data;

      sessionStorage.setItem("oauth_provider", provider);
      sessionStorage.setItem("oauth_reconnect", "true");

      window.location.href = authorization_url;
    } catch (error: any) {
      console.error("Failed to get OAuth URL:", error);
      const detail = error?.response?.data?.detail;
      const description =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail) && detail[0]?.msg
            ? detail[0].msg
            : typeof detail === "object" && detail?.message
              ? detail.message
              : "Please try again later";
      toast.error("Failed to start re-authentication", { description });
    }
  };

  const [activeTab, setActiveTab] = useState<string | null>(
    () => searchParams.get("tab") || "email",
  );

  const isActive = (tab: string) => activeTab === tab;
  const roleSet = new Set(roles ?? []);
  const isAdmin = roleSet.has("ADMIN");
  const emailModeUnchanged =
    useSharedMailboxDraft === Boolean(outgoingMailbox?.uses_official_email) &&
    officialEmailDraft.trim() ===
      (outgoingMailbox?.official_outgoing_email ?? "").trim();
  const isTrialUser = roleSet.has("TRIAL");
  const canDeleteUserOnly = roleSet.has("USER") || roleSet.has("ADMIN");
  const canDeleteAllData = !isTrialUser;
  const canConfirmDelete =
    deleteAcknowledge && deletePhrase.trim() === "DELETE";

  useEffect(() => {
    if (!isAdmin && activeTab === "danger-zone") {
      setActiveTab("email");
    }
  }, [isAdmin, activeTab]);

  const triggerDeleteFlow = (scope: DeleteUserDataScope) => {
    setDeleteScope(scope);
    setDeletePhrase("");
    setDeleteAcknowledge(false);
  };

  const handleDeleteData = () => {
    if (!deleteScope) return;
    deleteUserDataMutation.mutate(deleteScope, {
      onSuccess: async () => {
        await logout();
        toast.success("Account data deleted");
        window.location.href = "/login";
      },
      onError: (error: any) => {
        const detail = error?.response?.data?.detail;
        const message =
          typeof detail === "string"
            ? detail
            : detail?.message || "Failed to delete data";
        toast.error("Delete failed", { description: message });
      },
    });
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-linear-to-br from-slate-50 via-white to-emerald-50/30">
      <div className="mb-6 w-full shrink-0">
        <h1 className="mb-2 text-3xl font-bold text-gray-800">Settings</h1>
        <p className="text-gray-600">
          Manage your account preferences and email settings
        </p>
        {!canMutateOrg ? (
          <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            You have view-only access. Settings cannot be changed with this
            role.
          </p>
        ) : null}
      </div>

      <div className="flex min-h-0 w-full flex-1 gap-6 overflow-hidden">
        <Tabs
          value={activeTab}
          onChange={setActiveTab}
          orientation="vertical"
          variant="pills"
          radius="lg"
          className="flex h-full min-h-0 w-full min-w-0 gap-6 font-medium"
          classNames={{
            tab: "data-[active]:font-bold",
            root: "flex h-full min-h-0 min-w-0 flex-1 gap-6",
          }}
          styles={{
            tab: {
              padding: "10px 14px",
              margin: "2px 0",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              borderRadius: "10px",
              fontSize: "14px",
            },
            list: {
              width: "196px",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              backdropFilter: "blur(16px)",
              padding: "12px",
              borderRadius: "24px",
              border: `1px solid rgba(0, 0, 0, 0.05)`,
              boxShadow: "0 10px 30px -10px rgba(0, 0, 0, 0.1)",
              height: "auto",
              overflow: "visible",
              gap: "4px",
            },
          }}
        >
          <div className="h-full min-h-0 max-h-[calc(100dvh-11rem)] shrink-0 overflow-y-auto overflow-x-hidden overscroll-contain">
            <Tabs.List>
              <Tabs.Tab
                value="account"
                color="teal"
                leftSection={
                  <div
                    className={`p-1.5 rounded-lg ring-1 transition-colors ${
                      isActive("account")
                        ? "bg-teal-600 text-white ring-teal-600"
                        : "bg-teal-50 text-teal-600 ring-teal-100"
                    }`}
                  >
                    <IconUser size={16} stroke={2.5} />
                  </div>
                }
              >
                Account
              </Tabs.Tab>

              <div className="my-2 border-t border-gray-100" />

              <ManagerScope>
                <Tabs.Tab
                  value="users"
                  color="teal"
                  leftSection={
                    <div
                      className={`p-1.5 rounded-lg ring-1 transition-colors ${
                        isActive("users")
                          ? "bg-teal-600 text-white ring-teal-600"
                          : "bg-teal-50 text-teal-600 ring-teal-100"
                      }`}
                    >
                      <IconUsers size={16} stroke={2.5} />
                    </div>
                  }
                >
                  Users
                </Tabs.Tab>

                <Tabs.Tab
                  value="analytics"
                  color="teal"
                  leftSection={
                    <div
                      className={`p-1.5 rounded-lg ring-1 transition-colors ${
                        isActive("analytics")
                          ? "bg-teal-600 text-white ring-teal-600"
                          : "bg-teal-50 text-teal-600 ring-teal-100"
                      }`}
                    >
                      <IconChartBar size={16} stroke={2.5} />
                    </div>
                  }
                >
                  Analytics
                </Tabs.Tab>

                <Tabs.Tab
                  value="blocked-customers"
                  color="teal"
                  leftSection={
                    <div
                      className={`p-1.5 rounded-lg ring-1 transition-colors ${
                        isActive("blocked-customers")
                          ? "bg-teal-600 text-white ring-teal-600"
                          : "bg-teal-50 text-teal-600 ring-teal-100"
                      }`}
                    >
                      <IconBan size={16} stroke={2.5} />
                    </div>
                  }
                >
                  Blocked
                </Tabs.Tab>
              </ManagerScope>

              <div className="my-2 border-t border-gray-100" />

              <div
                role="presentation"
                className="px-3 pt-1 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400"
              >
                Email
              </div>

              <Tabs.Tab
                value="email"
                color="indigo"
                leftSection={
                  <div
                    className={`p-1.5 rounded-lg ring-1 transition-colors ${
                      isActive("email")
                        ? "bg-indigo-600 text-white ring-indigo-600"
                        : "bg-indigo-50 text-indigo-600 ring-indigo-100"
                    }`}
                  >
                    <IconMail size={16} stroke={2.5} />
                  </div>
                }
              >
                General
              </Tabs.Tab>

              <Tabs.Tab
                value="escalation"
                color="indigo"
                leftSection={
                  <div
                    className={`p-1.5 rounded-lg ring-1 transition-colors ${
                      isActive("escalation")
                        ? "bg-indigo-600 text-white ring-indigo-600"
                        : "bg-indigo-50 text-indigo-600 ring-indigo-100"
                    }`}
                  >
                    <IconEscalatorUp size={16} stroke={2.5} />
                  </div>
                }
              >
                Escalation
              </Tabs.Tab>

              <Tabs.Tab
                value="email-signature"
                color="indigo"
                leftSection={
                  <div
                    className={`p-1.5 rounded-lg ring-1 transition-colors ${
                      isActive("email-signature")
                        ? "bg-indigo-600 text-white ring-indigo-600"
                        : "bg-indigo-50 text-indigo-600 ring-indigo-100"
                    }`}
                  >
                    <IconSignature size={16} stroke={2.5} />
                  </div>
                }
              >
                Signature
              </Tabs.Tab>

              <Tabs.Tab
                value="email-sync"
                color="indigo"
                leftSection={
                  <div
                    className={`p-1.5 rounded-lg ring-1 transition-colors ${
                      isActive("email-sync")
                        ? "bg-indigo-600 text-white ring-indigo-600"
                        : "bg-indigo-50 text-indigo-600 ring-indigo-100"
                    }`}
                  >
                    <IconMailbox size={16} stroke={2.5} />
                  </div>
                }
              >
                Sync Status
              </Tabs.Tab>

              <div className="my-2 border-t border-gray-100" />
              <Tabs.Tab
                value="security"
                color="orange"
                leftSection={
                  <div
                    className={`p-1.5 rounded-lg ring-1 transition-colors ${
                      isActive("security")
                        ? "bg-orange-500 text-white ring-orange-500"
                        : "bg-orange-50 text-orange-600 ring-orange-100"
                    }`}
                  >
                    <IconShieldLock size={16} stroke={2.5} />
                  </div>
                }
              >
                Security
              </Tabs.Tab>

              <AdminScope>
                <Tabs.Tab
                  value="danger-zone"
                  color="red"
                  leftSection={
                    <div
                      className={`p-1.5 rounded-lg ring-1 transition-colors ${
                        isActive("danger-zone")
                          ? "bg-red-600 text-white ring-red-600"
                          : "bg-red-50 text-red-600 ring-red-100"
                      }`}
                    >
                      <IconTrash size={16} stroke={2.5} />
                    </div>
                  }
                >
                  Danger Zone
                </Tabs.Tab>
              </AdminScope>
            </Tabs.List>
          </div>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200/80 bg-white/90 shadow-xl backdrop-blur-xl">
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6 lg:px-10 lg:py-8">
              <Tabs.Panel value="account">
                <AccountProfileSubTab
                  userInfo={userInfo}
                  isSaving={isUpdatingUsername}
                  onSave={handleSaveUsername}
                />
              </Tabs.Panel>

              <ManagerScope>
                <Tabs.Panel value="users">
                  <OrgUsersPage />
                </Tabs.Panel>

                <Tabs.Panel value="analytics">
                  <DsoSettingsSubTab
                    organization={organization}
                    onSave={handleSaveDsoSettings}
                    isSaving={isUpdatingDsoSettings}
                  />
                </Tabs.Panel>

                <Tabs.Panel value="blocked-customers">
                  <BlockedCustomersSection />
                </Tabs.Panel>

                <Tabs.Panel value="escalation">
                  <EscalationSettingsTab />
                </Tabs.Panel>
              </ManagerScope>

              <Tabs.Panel value="email">
                {isAdmin && (
                  <div className="mb-6">
                    <EmailModeSettingsSubTab
                      useSharedMailbox={useSharedMailboxDraft}
                      onUseSharedMailboxChange={setUseSharedMailboxDraft}
                      officialEmailDraft={officialEmailDraft}
                      onOfficialEmailDraftChange={setOfficialEmailDraft}
                      onSave={handleSaveEmailMode}
                      isSaving={isSavingCollectionEmail}
                      unchanged={emailModeUnchanged}
                    />
                  </div>
                )}
                <EmailGeneralSettingsSubTab
                  organization={organization}
                  autoSendMode={autoSendMode}
                  setAutoSendMode={setAutoSendMode}
                  isUpdatingAutoEmailMode={isUpdatingAutoEmailMode}
                  handleSaveAutoSendMode={handleSaveAutoSendMode}
                  selectedLanguage={selectedLanguage}
                  setSelectedLanguage={setSelectedLanguage}
                  handleUpdateAllCustomersLanguage={
                    handleUpdateAllCustomersLanguage
                  }
                  updateAllCustomersLanguageMutation={
                    updateAllCustomersLanguageMutation
                  }
                  defaultLanguageQuery={defaultLanguageQuery}
                />
              </Tabs.Panel>

              <Tabs.Panel value="email-signature">
                <EmailSignatureSettings />
              </Tabs.Panel>

              <Tabs.Panel value="email-sync">
                <EmailSyncSubTab
                  syncStatus={syncStatus}
                  outgoingMailbox={outgoingMailbox}
                  userInfo={userInfo}
                  handleReAuthenticateEmail={handleReAuthenticateEmail}
                  onSyncNow={handleTriggerMailboxSync}
                  lastE2eRefresh={organization?.last_e2e_refresh_end}
                  isProcessingEmails={isProcessingEmails}
                />
              </Tabs.Panel>

              <Tabs.Panel value="security">
                <div className="space-y-6">
                  <SecurityMfaSection
                    mfaEnabled={organization?.mfa_enabled ?? false}
                    isUpdating={isUpdatingMfa}
                    onToggle={(enabled) =>
                      updateOrgMfa(enabled, {
                        onSuccess: () =>
                          toast.success(
                            enabled
                              ? "MFA enabled for your organization"
                              : "MFA disabled for your organization",
                          ),
                        onError: () =>
                          toast.error("Failed to update MFA setting"),
                      })
                    }
                  />
                  <SecurityMcpSection
                    mcpEnabled={organization?.mcp_enabled ?? true}
                    hasPriorityUrl={organization?.has_priority_url ?? false}
                    isUpdating={isUpdatingMcp}
                    canMutate={canMutateOrg}
                    onToggle={(enabled) =>
                      updateOrgMcp(enabled, {
                        onSuccess: () =>
                          toast.success(
                            enabled
                              ? "MCP enabled for your organization"
                              : "MCP disabled for your organization",
                          ),
                        onError: () =>
                          toast.error("Failed to update MCP setting"),
                      })
                    }
                  />
                </div>
              </Tabs.Panel>

              <AdminScope>
                <Tabs.Panel value="danger-zone">
                  <div className="space-y-5">
                    <div>
                      <h2 className="text-xl font-bold text-gray-800 mb-1">
                        Danger Zone
                      </h2>
                      <p className="text-sm text-gray-500">
                        Deleting data is irreversible. This action permanently
                        removes records from your account.
                      </p>
                    </div>

                    {isTrialUser ? (
                      <div className="rounded-xl border border-red-200 bg-red-50 p-5">
                        <p className="text-sm font-semibold text-red-800">
                          Trial account deletion
                        </p>
                        <p className="mt-1 text-sm text-red-700">
                          Trial users will delete all user and organization data
                          in one action.
                        </p>
                        <button
                          type="button"
                          onClick={() => triggerDeleteFlow("all")}
                          className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
                        >
                          Delete all my data
                        </button>
                      </div>
                    ) : (
                      <div className="grid gap-4 md:grid-cols-2">
                        {canDeleteUserOnly && (
                          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                            <p className="text-sm font-semibold text-amber-900">
                              Delete only my user data
                            </p>
                            <p className="mt-1 text-sm text-amber-800">
                              Removes your personal account data while keeping
                              the organization.
                            </p>
                            <button
                              type="button"
                              onClick={() => triggerDeleteFlow("user")}
                              className="mt-3 rounded-lg border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-900 transition hover:bg-amber-100"
                            >
                              Delete user data
                            </button>
                          </div>
                        )}

                        {canDeleteAllData && (
                          <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                            <p className="text-sm font-semibold text-red-900">
                              Delete user + organization data
                            </p>
                            <p className="mt-1 text-sm text-red-800">
                              Permanently removes all organization data and all
                              users.
                            </p>
                            <button
                              type="button"
                              onClick={() => triggerDeleteFlow("all")}
                              className="mt-3 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
                            >
                              Delete all data
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {deleteScope && (
                      <div className="rounded-xl border border-red-300 bg-white p-5">
                        <p className="text-sm font-semibold text-red-900">
                          Confirm destructive action
                        </p>
                        <p className="mt-1 text-sm text-red-800">
                          Type <span className="font-semibold">DELETE</span> and
                          confirm that this action is irreversible.
                        </p>
                        <input
                          type="text"
                          value={deletePhrase}
                          onChange={(e) => setDeletePhrase(e.target.value)}
                          placeholder="Type DELETE"
                          className="mt-3 w-full rounded-lg border border-red-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100"
                        />
                        <label className="mt-3 flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={deleteAcknowledge}
                            onChange={(e) =>
                              setDeleteAcknowledge(e.target.checked)
                            }
                          />
                          I understand this action cannot be undone.
                        </label>
                        <div className="mt-4 flex gap-3">
                          <button
                            type="button"
                            onClick={handleDeleteData}
                            disabled={
                              !canConfirmDelete ||
                              deleteUserDataMutation.isPending
                            }
                            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
                          >
                            {deleteUserDataMutation.isPending
                              ? "Deleting..."
                              : "Confirm delete"}
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteScope(null)}
                            disabled={deleteUserDataMutation.isPending}
                            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </Tabs.Panel>
              </AdminScope>
            </div>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
